<template>
  <div class="achievement-image-uploader">
    <div class="uploader-toolbar">
      <span class="format-help">上传一张 PNG/JPG/WebP，CMS 自动生成详情 WebP 和 320×320 列表缩略图。</span>
      <el-button
        v-if="completedCount === 2"
        link
        type="primary"
        :disabled="disabled || isUploading"
        @click="swapImages"
      >
        两张图放反了？一键互换
      </el-button>
    </div>
    <div class="achievement-display-images">
      <div v-for="state in states" :key="state.key" class="image-state-card">
        <div class="state-heading">
          <div class="state-title">{{ state.label }}</div>
          <el-tag size="small" :type="imageValue(state.key) ? 'success' : (state.required ? 'warning' : 'info')">
            {{ imageValue(state.key) ? '已上传' : (state.required ? '必填' : '可选') }}
          </el-tag>
        </div>
        <div class="state-help">{{ state.help }}</div>
        <el-upload
          action="/cms/v1/achievement-resource"
          drag
          :show-file-list="false"
          :disabled="disabled || uploading[state.key]"
          accept="image/png,image/jpeg,image/webp"
          :before-upload="file => beforeUpload(file, state.key)"
          :on-progress="() => setUploading(state.key, true)"
          :on-error="() => handleUploadError(state.key)"
          :on-success="handleUploadSuccess(state.key)"
        >
          <div v-if="imageValue(state.key)" class="preview-wrap">
            <img :src="resourceUrl(imageValue(state.key))" :alt="state.label" class="preview-image">
            <div class="replace-mask">{{ uploading[state.key] ? '上传中…' : '点击或拖入图片替换' }}</div>
          </div>
          <div v-else class="upload-placeholder">
            <span class="upload-icon">＋</span>
            <strong>{{ uploading[state.key] ? '上传中…' : '点击或拖入图片' }}</strong>
            <small>上传后可在此直接预览</small>
          </div>
        </el-upload>
        <div class="card-actions">
          <span class="upload-state-text">{{ imageValue(state.key) ? '详情图和缩略图已自动生成，记得发布图片资源' : '尚未上传' }}</span>
          <el-button
            v-if="imageValue(state.key)"
            link
            type="danger"
            :disabled="disabled || uploading[state.key]"
            @click="clearImage(state.key)"
          >
            移除
          </el-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {ElMessage} from 'element-plus';

const CDN_BASE = 'https://jigsaw-cdn.plutodesk.com';

export default {
  name: 'AchievementDisplayImageUploader',
  props: {
    modelValue: {type: Object, default: () => ({})},
    disabled: Boolean,
  },
  emits: ['update:modelValue', 'change', 'uploading'],
  data() {
    return {
      uploading: {locked: false, unlocked: false},
      states: [
        {key: 'locked', label: '锁定图', help: '上传后自动生成详情图和列表缩略图', required: true},
        {key: 'unlocked', label: '点亮图', help: '上传后自动生成详情图和列表缩略图', required: true},
      ],
    };
  },
  computed: {
    completedCount() {
      return ['locked', 'unlocked'].filter(key => this.imageValue(key)).length;
    },
    isUploading() {
      return Object.values(this.uploading).some(Boolean);
    },
  },
  watch: {
    isUploading(value) { this.$emit('uploading', value); },
  },
  methods: {
    imageValue(key) {
      return String(this.modelValue?.[key] || '').trim();
    },
    resourceUrl(resource) {
      const value = String(resource || '').trim();
      if (/^https:\/\//i.test(value)) return value;
      return `${CDN_BASE}/${value.replace(/^\/+/, '')}`;
    },
    beforeUpload(file, key) {
      if (!String(file?.type || '').startsWith('image/')) {
        ElMessage.error('只能上传图片文件');
        return false;
      }
      if (Number(file?.size || 0) > 10 * 1024 * 1024) {
        ElMessage.error('图片不能超过 10MB');
        return false;
      }
      this.setUploading(key, true);
      return true;
    },
    setUploading(key, value) {
      this.uploading[key] = value;
    },
    handleUploadSuccess(key) {
      return response => {
        this.setUploading(key, false);
        const detail = response?.data?.detail;
        const thumbnail = response?.data?.thumbnail;
        if (!detail || !thumbnail) {
          ElMessage.error('上传成功但未生成详情图和缩略图，请重试');
          return;
        }
        this.updateImage(key, detail, thumbnail);
        const label = this.states.find(state => state.key === key)?.label || '成就图';
        ElMessage.success(`${label}已上传`);
      };
    },
    handleUploadError(key) {
      this.setUploading(key, false);
      ElMessage.error('图片上传失败，请重试');
    },
    clearImage(key) {
      this.updateImage(key, '', '');
    },
    swapImages() {
      const next = {
        locked: this.imageValue('unlocked'),
        unlocked: this.imageValue('locked'),
        locked_thumbnail: this.imageValue('unlocked_thumbnail'),
        unlocked_thumbnail: this.imageValue('locked_thumbnail'),
      };
      this.$emit('update:modelValue', next);
      this.$emit('change', next);
      ElMessage.success('两张状态图已互换，请检查后发布图片资源');
    },
    updateImage(key, detail, thumbnail) {
      const next = {
        locked: this.imageValue('locked'),
        unlocked: this.imageValue('unlocked'),
        locked_thumbnail: this.imageValue('locked_thumbnail'),
        unlocked_thumbnail: this.imageValue('unlocked_thumbnail'),
        [key]: detail,
        [`${key}_thumbnail`]: thumbnail,
      };
      this.$emit('update:modelValue', next);
      this.$emit('change', next);
    },
  },
};
</script>

<style scoped>
.uploader-toolbar,
.state-heading,
.card-actions {
  align-items: center;
  display: flex;
  justify-content: space-between;
}

.uploader-toolbar {
  margin-bottom: 10px;
}

.format-help,
.upload-state-text {
  color: var(--el-text-color-secondary);
  font-size: 12px;
}

.achievement-display-images {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
}

.image-state-card {
  border: 1px solid var(--el-border-color);
  border-radius: 8px;
  padding: 12px;
  flex: 1;
  min-width: 260px;
}

.state-title {
  color: var(--el-text-color-primary);
  font-weight: 600;
}

.state-help {
  color: var(--el-text-color-secondary);
  font-size: 12px;
  margin: 4px 0 10px;
}

.image-state-card :deep(.el-upload),
.image-state-card :deep(.el-upload-dragger) {
  box-sizing: border-box;
  padding: 0;
  width: 100%;
}

.preview-wrap,
.upload-placeholder {
  align-items: center;
  border: 1px dashed var(--el-border-color);
  border-radius: 6px;
  display: flex;
  height: 132px;
  justify-content: center;
  overflow: hidden;
  position: relative;
  width: 100%;
}

.preview-image {
  height: 100%;
  object-fit: contain;
  width: 100%;
}

.replace-mask {
  align-items: center;
  background: rgb(0 0 0 / 55%);
  bottom: 0;
  color: #fff;
  display: flex;
  justify-content: center;
  left: 0;
  opacity: 0;
  position: absolute;
  right: 0;
  top: 0;
  transition: opacity 0.2s;
}

.preview-wrap:hover .replace-mask {
  opacity: 1;
}

@media (max-width: 900px) {
  .achievement-display-images {
    flex-direction: column;
  }

  .image-state-card {
    min-width: 0;
  }
}

.upload-placeholder {
  color: var(--el-color-primary);
  flex-direction: column;
  gap: 5px;
}

.upload-placeholder small {
  color: var(--el-text-color-secondary);
}

.upload-icon {
  font-size: 28px;
  font-weight: 300;
  line-height: 28px;
}

.card-actions {
  min-height: 28px;
  padding-top: 5px;
}
</style>
